import React, { useMemo, useState } from "react";
import { ChevronLeft, Eye, Inbox, GitBranch, Activity } from "lucide-react";
import { fmtDateTime } from "../../utils";
import { LEGACY_AGENT_ALIAS } from "../../checks";
import { FeedCol } from "./FeedCol";
import { SpineCol, type SpineCardEntry } from "./SpineCol";
import { RightCol } from "./RightCol";
import { PhaseRail } from "./PhaseRail";
import type { ActionLog, CompletedRun, SimEmail } from "../../types";

export interface ReplayViewProps {
  run: CompletedRun;
  onBack: () => void;
  isCompact: boolean;
}

/* Replay view — renders a completed run in read-only mode using the same
   Feed / Spine / Right column components but with noop handlers. */
export function ReplayView({ run, onBack, isCompact }: ReplayViewProps): JSX.Element {
  const rp = run.replay;
  const [activeWs, setActiveWs] = useState<string>(rp.matters[0] ? rp.matters[0].id : "unassoc");
  const [rightTab, setRightTab] = useState<string>("approvals");
  const [pane, setPane] = useState<string>("spine");
  const [feedOpen, setFeedOpen] = useState<number | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [showChecks, setShowChecks] = useState<Record<string, boolean>>({});
  const [expandedCheck, setExpandedCheck] = useState<Record<string, boolean>>({});
  const wsName = (id: string): string => rp.wsNameMap[id] || id;
  const emails: SimEmail[] = rp.processed.map((x) => x.email);
  const total = emails.length;
  const wsEntries = useMemo<Record<string, SpineCardEntry[]>>(() => {
    const m: Record<string, SpineCardEntry[]> = { unassoc: [] };
    rp.matters.forEach((w) => {
      m[w.id] = [];
    });
    rp.processed.forEach((x) => {
      (x.R.catalogs || []).forEach((c) => {
        if (m[c[0]]) m[c[0]].push({ p: x, conf: c[1] });
      });
    });
    return m;
  }, [rp]);
  const activeAgents = new Set<string>(
    rp.activeAgents.map((id) => LEGACY_AGENT_ALIAS[id] || id),
  );
  const actionsByAgent = useMemo<Record<string, ActionLog[]>>(() => {
    const g: Record<string, ActionLog[]> = {};
    rp.actionsLog
      .filter((a) => !a.reproc && !!a.agent)
      .forEach((a) => {
        const key = LEGACY_AGENT_ALIAS[a.agent as string] || (a.agent as string);
        if (!activeAgents.has(key)) return;
        (g[key] = g[key] || []).push(a);
      });
    return g;
  }, [rp]);
  const reprocs = rp.actionsLog.filter((a) => a.reproc);
  const agentSkips = useMemo<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    rp.processed.forEach((p) =>
      p.checks.forEach((c) => {
        if (c.skipped) m[c.id] = c.reason || "trigger did not fire";
        else delete m[c.id];
      }),
    );
    return m;
  }, [rp]);
  const pendingCount = rp.approvals.filter((a) => a.status === "pending").length;
  const evalAttached = !!rp.evalResult && !rp.evalResult.noEval;
  const noopStr = (_: string): void => {};
  const noopBool = (_: boolean): void => {};
  const noopDecide = (_k: string, _ok: boolean, _chosen?: string): void => {};
  const noopThresh = (_id: string, _v: number): void => {};
  const routedWs = (emailId: number): string => {
    const p = rp.processed.find((x) => x.email.id === emailId);
    return (p && p.R.catalogs[0] && p.R.catalogs[0][0]) || "unassoc";
  };
  const Feed = (
    <FeedCol
      emails={emails}
      cursor={total}
      runState="done"
      feedOpen={feedOpen}
      setFeedOpen={setFeedOpen}
      routedWs={routedWs}
      onView={(top) => {
        setActiveWs(top);
        if (isCompact) setPane("spine");
      }}
    />
  );
  const Spine = (
    <SpineCol
      mattersInScope={rp.matters}
      wsName={wsName}
      wsEntries={wsEntries}
      activeWs={activeWs}
      setActiveWs={setActiveWs}
      filter={filter}
      setFilter={setFilter}
      showChecks={showChecks}
      setShowChecks={setShowChecks}
      expandedCheck={expandedCheck}
      setExpandedCheck={setExpandedCheck}
      thresholds={rp.thresholds}
      CM={rp.CM}
      cursor={total}
      total={total}
    />
  );
  const Right = (
    <RightCol
      readOnly
      rightTab={rightTab}
      setRightTab={setRightTab}
      pendingCount={pendingCount}
      evalAttached={evalAttached}
      approvals={rp.approvals}
      decide={noopDecide}
      decideAll={noopBool}
      wsName={wsName}
      memos={rp.memos}
      activeWs={activeWs}
      setActiveWs={setActiveWs}
      wsDefs={rp.matters}
      actionsByAgent={actionsByAgent}
      agentSkips={agentSkips}
      documentRegister={rp.documentRegister}
      trackers={rp.trackers}
      trackerKeys={rp.trackerKeys ? new Set(rp.trackerKeys) : undefined}
      routedWs={routedWs}
      reprocs={reprocs}
      activeAgents={activeAgents}
      toggleAgent={noopStr}
      toggledOff={new Set<string>(rp.toggledOff)}
      toggleCheck={noopStr}
      thresholds={rp.thresholds}
      setThreshold={noopThresh}
      checkList={rp.checkList}
      cost={rp.cost}
      evalResult={rp.evalResult}
      evalName={rp.evalName}
    />
  );
  return (
    <main className="page" id="main">
      <div className="pagehd">
        <div className="runhd">
          <button className="btn btn-ghost btn-sm" onClick={onBack}>
            <ChevronLeft size={14} /> History
          </button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1 className="h1" style={{ fontSize: 20 }}>
              {rp.corpus}
            </h1>
            <div className="psub">
              {run.id} · {fmtDateTime(run.when)} ·{" "}
              <span
                className="ttrigger"
                data-tip="Pinned to this check-set version; reproducible across runs."
              >
                {rp.checkVersion}
              </span>{" "}
              · {rp.matters.length} matter{rp.matters.length === 1 ? "" : "s"}
              {evalAttached ? " · " + rp.evalName : ""}
            </div>
          </div>
        </div>
      </div>
      <div className="donebar">
        <Eye size={14} color="var(--muted)" /> Replay of a completed run — read-only. Click through
        the feed, spine, checks and panels exactly as they ran.
      </div>
      <PhaseRail
        emails={emails}
        processed={rp.processed}
        memos={rp.memos}
        matters={rp.matters}
      />
      {isCompact ? (
        <>
          <div className="tabbar">
            <button className={pane === "feed" ? "on" : ""} onClick={() => setPane("feed")}>
              <Inbox size={15} /> Feed
            </button>
            <button className={pane === "spine" ? "on" : ""} onClick={() => setPane("spine")}>
              <GitBranch size={15} /> Spine
            </button>
            <button className={pane === "panel" ? "on" : ""} onClick={() => setPane("panel")}>
              <Activity size={15} /> Panel
            </button>
          </div>
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderTop: "1px solid var(--bd)",
            }}
          >
            {pane === "feed" ? Feed : pane === "spine" ? Spine : Right}
          </div>
        </>
      ) : (
        <div className="simwrap">
          {Feed}
          {Spine}
          {Right}
        </div>
      )}
    </main>
  );
}
